import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Package,
  ShoppingCart,
  Layers,
  MessageSquareQuote,
  DollarSign,
  TrendingUp,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  Truck,
  XCircle,
  AlertCircle,
  CalendarDays,
  Users,
  Settings,
  PlusCircle,
  Check,
  BarChart3,
} from 'lucide-react';
import clsx from 'clsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { useAdminStats } from '../../services/adminService';
import { formatPrice } from '../../lib/utils';

/* ── Register Chart.js components ── */
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

/* ── Status config (reused from other admin pages) ── */
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#F59E0B', icon: Clock },
  confirmed: { label: 'Confirmed', color: '#3B82F6', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', color: '#8B5CF6', icon: Truck },
  delivered: { label: 'Delivered', color: '#22C55E', icon: CheckCircle2 },
  completed: { label: 'Completed', color: '#059669', icon: Check },
  cancelled: { label: 'Cancelled', color: '#EF4444', icon: XCircle },
};

const STAT_CARDS = [
  { key: 'orderCount', label: 'Total Orders', icon: ShoppingCart, color: 'text-brand-red', bg: 'bg-red-50', link: '/admin/orders' },
  { key: 'totalRevenue', label: 'Total Revenue', icon: DollarSign, color: 'text-green-600', bg: 'bg-green-50', link: '/admin/orders', isCurrency: true },
  { key: 'productCount', label: 'Products', icon: Package, color: 'text-blue-600', bg: 'bg-blue-50', link: '/admin/products' },
  { key: 'categoryCount', label: 'Categories', icon: Layers, color: 'text-amber-600', bg: 'bg-amber-50', link: '/admin/categories' },
];

/* ── Shared chart styling ── */
const chartFont = { family: "'Inter', sans-serif", size: 11 };

const baseGridOpts = {
  color: 'rgba(0,0,0,0.04)',
  drawBorder: false,
};

/**
 * Reusable card wrapper with consistent styling.
 */
function DashCard({ children, className, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      className={clsx('bg-brand-white border border-ui-border rounded-2xl', className)}
    >
      {children}
    </motion.div>
  );
}

/**
 * Admin dashboard — KPI cards, charts, recent orders, and quick actions.
 */
export default function AdminDashboardPage() {
  const { data: stats, isLoading } = useAdminStats();

  /* ── Chart data ── */

  // Orders per day — Line chart
  const dailyOrdersChart = useMemo(() => {
    if (!stats?.dailyOrders) return null;
    return {
      labels: stats.dailyOrders.map((d) => d.label),
      datasets: [
        {
          label: 'Orders',
          data: stats.dailyOrders.map((d) => d.count),
          borderColor: '#D32323',
          backgroundColor: 'rgba(211,35,35,0.08)',
          fill: true,
          tension: 0.4,
          borderWidth: 2.5,
          pointBackgroundColor: '#D32323',
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [stats?.dailyOrders]);

  // Revenue per day — Bar chart
  const dailyRevenueChart = useMemo(() => {
    if (!stats?.dailyRevenue) return null;
    return {
      labels: stats.dailyRevenue.map((d) => d.label),
      datasets: [
        {
          label: 'Revenue (KES)',
          data: stats.dailyRevenue.map((d) => d.revenue),
          backgroundColor: 'rgba(22,163,74,0.7)',
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 40,
        },
      ],
    };
  }, [stats?.dailyRevenue]);

  // Orders by status — Doughnut
  const statusChart = useMemo(() => {
    if (!stats?.ordersByStatus) return null;
    const keys = Object.keys(stats.ordersByStatus);
    if (keys.length === 0) return null;
    return {
      labels: keys.map((k) => STATUS_CONFIG[k]?.label || k),
      datasets: [
        {
          data: keys.map((k) => stats.ordersByStatus[k]),
          backgroundColor: keys.map((k) => STATUS_CONFIG[k]?.color || '#9CA3AF'),
          borderWidth: 0,
          spacing: 2,
        },
      ],
    };
  }, [stats?.ordersByStatus]);

  // Products by category — Horizontal bar
  const categoryChart = useMemo(() => {
    if (!stats?.productsByCategory) return null;
    return {
      labels: stats.productsByCategory.map((c) => c.category),
      datasets: [
        {
          label: 'Products',
          data: stats.productsByCategory.map((c) => c.count),
          backgroundColor: [
            'rgba(211,35,35,0.75)',
            'rgba(59,130,246,0.75)',
            'rgba(245,158,11,0.75)',
            'rgba(139,92,246,0.75)',
            'rgba(34,197,94,0.75)',
            'rgba(236,72,153,0.75)',
          ],
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 28,
        },
      ],
    };
  }, [stats?.productsByCategory]);

  /* ── Chart options ── */
  const lineOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1F1F1F',
        titleFont: chartFont,
        bodyFont: chartFont,
        cornerRadius: 8,
        padding: 10,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: chartFont, color: '#9CA3AF' } },
      y: {
        grid: baseGridOpts,
        ticks: { font: chartFont, color: '#9CA3AF', stepSize: 1, beginAtZero: true },
      },
    },
  };

  const barOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1F1F1F',
        titleFont: chartFont,
        bodyFont: chartFont,
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: (ctx) => `KES ${ctx.raw.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: chartFont, color: '#9CA3AF' } },
      y: {
        grid: baseGridOpts,
        ticks: {
          font: chartFont,
          color: '#9CA3AF',
          callback: (v) => `${(v / 1000).toFixed(0)}k`,
          beginAtZero: true,
        },
      },
    },
  };

  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { ...chartFont, size: 10 }, padding: 12, usePointStyle: true, pointStyleWidth: 8 },
      },
      tooltip: {
        backgroundColor: '#1F1F1F',
        titleFont: chartFont,
        bodyFont: chartFont,
        cornerRadius: 8,
        padding: 10,
      },
    },
  };

  const horizontalBarOpts = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1F1F1F',
        titleFont: chartFont,
        bodyFont: chartFont,
        cornerRadius: 8,
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: baseGridOpts,
        ticks: { font: chartFont, color: '#9CA3AF', stepSize: 1, beginAtZero: true },
      },
      y: { grid: { display: false }, ticks: { font: chartFont, color: '#6B7280' } },
    },
  };

  return (
    <div className="space-y-6">
      {/* ── Page header ── */}
      <div>
        <h1 className="text-2xl font-extrabold text-brand-dark">Dashboard</h1>
        <p className="text-sm text-brand-gray mt-1">
          Welcome back. Here's an overview of your restaurant.
        </p>
      </div>

      {/* ═══════════════════════════════════
       * ROW 1 — KPI stat cards
       * ═══════════════════════════════════ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map((card, i) => {
          const Icon = card.icon;
          let value = isLoading ? '—' : (stats?.[card.key] ?? 0);
          if (!isLoading && card.isCurrency) value = formatPrice(value);
          return (
            <motion.div
              key={card.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
            >
              <Link
                to={card.link}
                className="block bg-brand-white border border-ui-border rounded-2xl p-5 hover:shadow-md transition-shadow group"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', card.bg)}>
                    <Icon size={20} className={card.color} />
                  </div>
                  <ArrowUpRight size={16} className="text-ui-muted group-hover:text-brand-dark transition-colors" />
                </div>
                <p className="text-2xl font-extrabold text-brand-dark">{value}</p>
                <p className="text-xs font-medium text-brand-gray mt-0.5">{card.label}</p>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* ═══════════════════════════════════
       * ROW 2 — Highlight widgets
       * ═══════════════════════════════════ */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Today's orders */}
        <DashCard className="p-5" delay={0.15}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-red/10 flex items-center justify-center flex-shrink-0">
              <CalendarDays size={20} className="text-brand-red" />
            </div>
            <div>
              <p className="text-xs font-medium text-brand-gray">Today's Orders</p>
              <p className="text-xl font-extrabold text-brand-dark">
                {isLoading ? '—' : stats?.todayOrderCount ?? 0}
              </p>
            </div>
          </div>
        </DashCard>

        {/* Today's revenue */}
        <DashCard className="p-5" delay={0.2}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
              <TrendingUp size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-brand-gray">Today's Revenue</p>
              <p className="text-xl font-extrabold text-brand-dark">
                {isLoading ? '—' : formatPrice(stats?.todayRevenue ?? 0)}
              </p>
            </div>
          </div>
        </DashCard>

        {/* Pending orders */}
        <DashCard className="p-5" delay={0.25}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <AlertCircle size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-brand-gray">Pending Orders</p>
              <p className="text-xl font-extrabold text-brand-dark">
                {isLoading ? '—' : stats?.pendingCount ?? 0}
              </p>
            </div>
            {!isLoading && (stats?.pendingCount ?? 0) > 0 && (
              <Link
                to="/admin/orders"
                className="ml-auto text-[10px] font-bold text-brand-red bg-red-50 px-2 py-1 rounded-lg hover:bg-red-100 transition-colors"
              >
                View
              </Link>
            )}
          </div>
        </DashCard>
      </div>

      {/* ═══════════════════════════════════
       * ROW 3 — Charts row (orders trend + revenue)
       * ═══════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Orders trend — Line chart */}
        <DashCard className="p-5" delay={0.3}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-brand-dark">Orders (Last 7 Days)</h3>
              <p className="text-[10px] text-brand-gray mt-0.5">Daily order volume</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <BarChart3 size={14} className="text-brand-red" />
            </div>
          </div>
          <div className="h-52">
            {isLoading || !dailyOrdersChart ? (
              <div className="h-full bg-brand-light/50 rounded-xl animate-pulse" />
            ) : (
              <Line data={dailyOrdersChart} options={lineOpts} />
            )}
          </div>
        </DashCard>

        {/* Revenue — Bar chart */}
        <DashCard className="p-5" delay={0.35}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-brand-dark">Revenue (Last 7 Days)</h3>
              <p className="text-[10px] text-brand-gray mt-0.5">Daily earnings breakdown</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
              <DollarSign size={14} className="text-green-600" />
            </div>
          </div>
          <div className="h-52">
            {isLoading || !dailyRevenueChart ? (
              <div className="h-full bg-brand-light/50 rounded-xl animate-pulse" />
            ) : (
              <Bar data={dailyRevenueChart} options={barOpts} />
            )}
          </div>
        </DashCard>
      </div>

      {/* ═══════════════════════════════════
       * ROW 4 — Status distribution + Products by category
       * ═══════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Order status — Doughnut */}
        <DashCard className="p-5" delay={0.4}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-brand-dark">Order Status</h3>
              <p className="text-[10px] text-brand-gray mt-0.5">Distribution by status</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
              <ShoppingCart size={14} className="text-purple-600" />
            </div>
          </div>
          <div className="h-56 flex items-center justify-center">
            {isLoading || !statusChart ? (
              <div className="w-40 h-40 bg-brand-light/50 rounded-full animate-pulse" />
            ) : (
              <Doughnut data={statusChart} options={doughnutOpts} />
            )}
          </div>
          {/* Status breakdown numbers */}
          {!isLoading && stats?.ordersByStatus && (
            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-ui-border">
              {Object.entries(stats.ordersByStatus).map(([key, count]) => {
                const cfg = STATUS_CONFIG[key];
                const Icon = cfg?.icon || Clock;
                return (
                  <div key={key} className="flex items-center gap-1.5">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cfg?.color || '#9CA3AF' }}
                    />
                    <span className="text-[10px] font-medium text-brand-gray truncate">
                      {cfg?.label || key}
                    </span>
                    <span className="text-[10px] font-bold text-brand-dark ml-auto">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </DashCard>

        {/* Products by category — Horizontal bar */}
        <DashCard className="p-5" delay={0.45}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-brand-dark">Products by Category</h3>
              <p className="text-[10px] text-brand-gray mt-0.5">Items per menu section</p>
            </div>
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Layers size={14} className="text-amber-600" />
            </div>
          </div>
          <div className="h-56">
            {isLoading || !categoryChart ? (
              <div className="h-full bg-brand-light/50 rounded-xl animate-pulse" />
            ) : (
              <Bar data={categoryChart} options={horizontalBarOpts} />
            )}
          </div>
          {/* Category totals */}
          {!isLoading && stats?.productsByCategory && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-ui-border">
              {stats.productsByCategory.map((c) => (
                <span
                  key={c.category}
                  className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-dark bg-brand-light px-2 py-1 rounded-lg"
                >
                  {c.category}
                  <span className="text-brand-gray">({c.count})</span>
                </span>
              ))}
            </div>
          )}
        </DashCard>
      </div>

      {/* ═══════════════════════════════════
       * ROW 5 — Recent orders + Revenue by status
       * ═══════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent orders */}
        <DashCard className="lg:col-span-2 p-5" delay={0.5}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-brand-dark">Recent Orders</h3>
            <Link
              to="/admin/orders"
              className="text-xs font-bold text-brand-red hover:underline"
            >
              View all
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-8 h-8 bg-brand-light rounded-lg" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 bg-brand-light rounded w-1/3" />
                    <div className="h-2.5 bg-brand-light rounded w-1/4" />
                  </div>
                  <div className="h-5 bg-brand-light rounded w-16" />
                </div>
              ))}
            </div>
          ) : !stats?.recentOrders?.length ? (
            <div className="py-8 text-center">
              <ShoppingCart size={32} className="text-ui-muted mx-auto mb-2" />
              <p className="text-sm text-brand-gray">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {stats.recentOrders.map((order) => {
                const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                const Icon = cfg.icon;
                return (
                  <Link
                    key={order.id}
                    to="/admin/orders"
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-brand-light/50 transition-colors group"
                  >
                    {/* Status dot */}
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${cfg.color}15` }}
                    >
                      <Icon size={14} style={{ color: cfg.color }} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-brand-dark truncate">
                        {order.customer_name}
                      </p>
                      <p className="text-[10px] text-brand-gray mt-0.5">
                        {order.product_snapshot?.title || 'Product'} × {order.quantity || 1}
                        &nbsp;&bull;&nbsp;
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>

                    {/* Price + status */}
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-extrabold text-brand-dark">
                        {formatPrice(order.total_price)}
                      </p>
                      <span
                        className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded mt-0.5"
                        style={{
                          backgroundColor: `${cfg.color}15`,
                          color: cfg.color,
                        }}
                      >
                        {cfg.label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </DashCard>

        {/* Revenue by status */}
        <DashCard className="p-5" delay={0.55}>
          <h3 className="text-sm font-bold text-brand-dark mb-4">Revenue by Status</h3>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 bg-brand-light rounded-lg animate-pulse" />
              ))}
            </div>
          ) : !stats?.revenueByStatus || Object.keys(stats.revenueByStatus).length === 0 ? (
            <div className="py-8 text-center">
              <DollarSign size={32} className="text-ui-muted mx-auto mb-2" />
              <p className="text-sm text-brand-gray">No revenue data</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(stats.revenueByStatus)
                .sort((a, b) => b[1] - a[1])
                .map(([key, rev]) => {
                  const cfg = STATUS_CONFIG[key];
                  const pct = stats.totalRevenue > 0 ? (rev / stats.totalRevenue) * 100 : 0;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-brand-gray">
                          {cfg?.label || key}
                        </span>
                        <span className="text-xs font-bold text-brand-dark">
                          {formatPrice(rev)}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-brand-light rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.6, ease: 'easeOut' }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: cfg?.color || '#9CA3AF' }}
                        />
                      </div>
                    </div>
                  );
                })}

              {/* Total */}
              <div className="pt-3 mt-3 border-t border-ui-border flex items-center justify-between">
                <span className="text-xs font-bold text-brand-gray">Total</span>
                <span className="text-sm font-extrabold text-brand-dark">
                  {formatPrice(stats.totalRevenue)}
                </span>
              </div>
            </div>
          )}
        </DashCard>
      </div>

      {/* ═══════════════════════════════════
       * ROW 6 — Quick actions
       * ═══════════════════════════════════ */}
      <DashCard className="p-5" delay={0.6}>
        <h3 className="text-sm font-bold text-brand-dark mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Add Product', to: '/admin/products/new', icon: PlusCircle, color: 'bg-blue-50 text-blue-600' },
            { label: 'View Orders', to: '/admin/orders', icon: ShoppingCart, color: 'bg-red-50 text-brand-red' },
            { label: 'Categories', to: '/admin/categories', icon: Layers, color: 'bg-amber-50 text-amber-600' },
            { label: 'Settings', to: '/admin/settings', icon: Settings, color: 'bg-gray-50 text-brand-gray' },
          ].map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.to}
                to={action.to}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-ui-border hover:bg-brand-light hover:shadow-sm transition-all text-center group"
              >
                <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', action.color)}>
                  <Icon size={18} />
                </div>
                <span className="text-xs font-semibold text-brand-dark group-hover:text-brand-red transition-colors">
                  {action.label}
                </span>
              </Link>
            );
          })}
        </div>
      </DashCard>
    </div>
  );
}
